/**
 * Markets API client for retrieving active markets
 */

import { config } from '@/config'

const API_BASE_URL = config.apiUrl

/** "Anywhere else": every "Other – {state}" area, including states first seen later */
export const ANYWHERE_ELSE = 'OTH_*'
export const ANYWHERE_ELSE_LABEL = 'Anywhere else'
export const ANYWHERE_ELSE_HELPER = 'Better chance to make the first offer'

/** Every live market plus Anywhere else; saved buy boxes store this as [] */
export function isEverywhere(markets: string[], liveCodes: string[]): boolean {
  return liveCodes.length > 0 && markets.includes(ANYWHERE_ELSE) && liveCodes.every((code) => markets.includes(code))
}

/**
 * Market response data
 */
export interface Market {
  code: string
  label: string
  state: string
  /** 'upcoming' markets are not live yet; investors can still add them to a buy box */
  status?: 'live' | 'upcoming'
  /** Market centre, used to tag a property with its nearest market */
  latitude?: number
  longitude?: number
  /** Every [lat, lng] centre; markets spanning two cities (DFW) have more than one */
  centres?: [number, number][]
}

/**
 * Get list of active markets, optionally followed by upcoming ones
 *
 * This endpoint is public and does not require authentication.
 *
 * @returns List of markets
 * @throws Error if request fails
 */
export async function getMarkets(options: { includeUpcoming?: boolean } = {}): Promise<Market[]> {
  const url = `${API_BASE_URL}/markets${options.includeUpcoming ? '?include_upcoming=true' : ''}`

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    })

    if (!response.ok) {
      throw new Error(`Failed to fetch markets: ${response.statusText}`)
    }

    return await response.json()
  } catch (error) {
    if (error instanceof TypeError) {
      throw new Error('Network error - unable to reach API')
    }
    throw error
  }
}
